import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button } from './ui';

interface ExportResponse {
  filePath: string;
}

interface ExportProgress {
  current: number;
  total: number;
  percentage: number;
  status: 'initializing' | 'counting' | 'processing' | 'finalizing' | 'uploading' | 'emailing' | 'completed' | 'error' | 'cancelled';
  message: string;
  phase?: string;
  elapsed_seconds?: number;
  elapsed_formatted?: string;
  time_remaining?: {
    estimated_seconds: number;
    estimated_minutes: number;
    formatted_time: string;
  };
}

const DataExport: React.FC = () => {
  const [exportResult, setExportResult] = useState<string>('');
  const [callDateFrom, setCallDateFrom] = useState<string>('');
  const [callDateTo, setCallDateTo] = useState<string>('');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const { apiRequest } = useAuth();

  const exportCsv = async () => {
    try {
      const response = await apiRequest<ExportResponse>({
        url: '/ui/admin/cancellation-processor/export-csv',
        method: 'POST'
      });
      setExportResult(`CSV created successfully at: ${response.filePath}`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setExportResult('Failed to export CSV');
    }
  };

  // Direct download export - DEPRECATED: Now using email export only
  // const handleExportDatabase = async () => { ... };

  const handleExportDatabaseEmail = async () => {
    try {
      console.log('Starting database export with email...');
      setIsExporting(true);
      setExportResult('');

      // Use fetch to initiate async export and get export ID
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      console.log('Calling email export endpoint:', `${apiUrl}/ui/admin/export-database-email`);

      // Make POST request to initiate export with email
      const response = await fetch(`${apiUrl}/ui/admin/export-database-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // Backend will use authenticated user's email
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Email export initiation failed:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get JSON response with export ID
      const data = await response.json();
      console.log('Received email export response:', data);

      // Show success message - user can navigate away
      setExportResult('Your export has been started. You should receive an email with a download link in 20-30 minutes. You can navigate away from this page.');
      setIsExporting(false);

      // Clear message after 10 seconds
      setTimeout(() => setExportResult(''), 10000);

    } catch (error) {
      console.error('Error exporting database with email:', error);
      setExportResult('Failed to start email export. Please try again.');
      setIsExporting(false);

      // Clear error message after 10 seconds
      setTimeout(() => setExportResult(''), 10000);
    }
  };

  const handleExportPaymentsReport = async () => {
    try {
      console.log('Starting payments report export...');
      setIsExporting(true);
      setExportResult('');

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${apiUrl}/ui/admin/export-payments-report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // Backend will use authenticated user's email
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Payments report initiation failed:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received payments report response:', data);

      setExportResult('Your payments report has been started. You will receive an email with a download link when it is ready. You can navigate away from this page.');
      setIsExporting(false);

      setTimeout(() => setExportResult(''), 10000);

    } catch (error) {
      console.error('Error starting payments report:', error);
      setExportResult('Failed to start payments report. Please try again.');
      setIsExporting(false);

      setTimeout(() => setExportResult(''), 10000);
    }
  };

  const handleExportStudentLoanEmails = async () => {
    try {
      console.log('Starting student loan email export...');
      setIsExporting(true);
      setExportResult('');

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const response = await fetch(`${apiUrl}/ui/admin/export-student-loan-emails`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Student loan export initiation failed:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received student loan export response:', data);

      setExportResult('Student loan export started. This searches all credit reports and may take some time. You will receive an email with the results.');
      setIsExporting(false);

      setTimeout(() => setExportResult(''), 15000);

    } catch (error) {
      console.error('Error starting student loan export:', error);
      setExportResult('Failed to start student loan export. Please try again.');
      setIsExporting(false);

      setTimeout(() => setExportResult(''), 10000);
    }
  };

  const handleExportCallData = async () => {
    try {
      // Validate date range
      if (!callDateFrom || !callDateTo) {
        setExportResult('Please select both start and end dates for the call data export.');
        return;
      }

      const fromDate = new Date(callDateFrom);
      const toDate = new Date(callDateTo);

      if (fromDate > toDate) {
        setExportResult('Start date must be before end date.');
        return;
      }

      setIsExporting(true);
      setExportResult('');

      // Use fetch directly for blob response
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000';

      const params = new URLSearchParams({
        date_from: callDateFrom,
        date_to: callDateTo
      });

      const response = await fetch(`${apiUrl}/ui/admin/export-call-data?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // Get blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `call_data_export_${callDateFrom}_to_${callDateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportResult('Call data exported successfully!');
      setTimeout(() => setExportResult(''), 5000);
    } catch (error) {
      console.error('Error exporting call data:', error);
      setExportResult(`Failed to export call data: ${error instanceof Error ? error.message : 'Please try again.'}`);
    } finally {
      setIsExporting(false);
    }
  };


  return (
    <div className="data-export-container">
      {/* Export Data Section */}
      <Card title="Export Data">
        <div className="export-options">
          <div className="export-option">
            <h4>Cancellations CSV Export</h4>
            <p className="description">Export cancellation processor data to CSV format.</p>
            <Button onClick={exportCsv} variant="primary" disabled={isExporting}>
              Export Cancellations CSV
            </Button>
          </div>

          <div className="export-option">
            <h4>Claims Excel Export</h4>
            <p className="description">Export all claims from the database to an Excel file with comprehensive formatting and summary statistics. Large exports may take 20-30 minutes to process. The export will be emailed to your registered email address with a download link valid for 7 days. You can navigate away from this page while the export processes.</p>
            <Button
              onClick={handleExportDatabaseEmail}
              variant="primary"
              disabled={isExporting}
            >
              {isExporting ? 'Starting Export...' : 'Export All Claims to Excel'}
            </Button>
          </div>

          <div className="export-option">
            <h4>Call Data Export</h4>
            <p className="description">Export call records within a date range, including call duration, participants, answered status, and notes.</p>
            <div className="date-range-inputs">
              <div className="date-input-group">
                <label htmlFor="callDateFrom">From Date:</label>
                <input
                  type="date"
                  id="callDateFrom"
                  value={callDateFrom}
                  onChange={(e) => setCallDateFrom(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="date-input-group">
                <label htmlFor="callDateTo">To Date:</label>
                <input
                  type="date"
                  id="callDateTo"
                  value={callDateTo}
                  onChange={(e) => setCallDateTo(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>
            <Button
              onClick={handleExportCallData}
              variant="primary"
              disabled={!callDateFrom || !callDateTo || isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export Call Data to Excel'}
            </Button>
          </div>

          <div className="export-option">
            <h4>Payments Report</h4>
            <p className="description">Export every cancellation payment we hold — one row per payment plus separate sheets for settlements, checkout attempts and status history, with a summary. Compiled in the background and emailed to your registered email address with a download link valid for 7 days. You can navigate away while it processes.</p>
            <Button
              onClick={handleExportPaymentsReport}
              variant="primary"
              disabled={isExporting}
            >
              {isExporting ? 'Starting Export...' : 'Export Payments Report'}
            </Button>
          </div>

          <div className="export-option">
            <h4>Student Loan Email Export</h4>
            <p className="description">Search all credit reports for claims containing "Student Loan" or "Student" and export a deduplicated list of email addresses. This process fetches each credit report individually and may take considerable time depending on the number of claims.</p>
            <Button
              onClick={handleExportStudentLoanEmails}
              variant="primary"
              disabled={isExporting}
            >
              {isExporting ? 'Starting Export...' : 'Export Student Loan Emails'}
            </Button>
          </div>
        </div>

        {exportResult && (
          <div className={`export-result ${exportResult.includes('Failed') ? 'error' : 'success'}`}>
            <span className="result-icon">{exportResult.includes('Failed') ? '⚠️' : '✅'}</span>
            {exportResult}
          </div>
        )}
      </Card>

      <style>{`
        .data-export-container {
          width: 100%;
          position: relative;
        }

        .export-section {
          background: white;
          padding: 25px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          margin-bottom: 20px;
        }

        .export-section h3 {
          margin-top: 0;
          margin-bottom: 20px;
          color: #333;
          font-size: 1.5em;
        }

        .export-options {
          display: grid;
          gap: 20px;
          margin-bottom: 20px;
        }

        .export-option {
          padding: 20px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          background-color: #f9f9f9;
          transition: box-shadow 0.2s;
        }

        .export-option:hover {
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .export-option h4 {
          margin: 0 0 8px 0;
          color: #444;
          font-size: 1.1em;
        }

        .export-option .description {
          color: #666;
          font-size: 0.9em;
          margin: 0 0 15px 0;
          line-height: 1.4;
        }

        .export-btn {
          padding: 10px 24px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
          font-size: 0.95em;
          transition: all 0.2s;
          background-color: #007bff;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .export-btn:hover:not(:disabled) {
          background-color: #0056b3;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .export-btn:active {
          transform: translateY(0);
          box-shadow: 0 1px 2px rgba(0,0,0,0.2);
        }

        .export-btn.success {
          background-color: #28a745;
        }

        .export-btn.success:hover:not(:disabled) {
          background-color: #218838;
        }

        .export-btn.warning {
          background-color: #ffc107;
          color: #212529;
        }

        .export-btn.warning:hover:not(:disabled) {
          background-color: #e0a800;
        }

        .export-btn.info {
          background-color: #17a2b8;
          color: white;
        }

        .export-btn.info:hover:not(:disabled) {
          background-color: #138496;
        }

        .export-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
          transform: none;
        }

        .date-range-inputs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }

        .date-input-group {
          display: flex;
          flex-direction: column;
        }

        .date-input-group label {
          font-size: 0.9em;
          color: #555;
          margin-bottom: 5px;
          font-weight: 500;
        }

        .date-input-group input[type="date"] {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 0.95em;
          transition: border-color 0.2s;
        }

        .date-input-group input[type="date"]:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
        }

        .date-input-group input[type="date"]:disabled {
          background: #f0f0f0;
          cursor: not-allowed;
        }

        @media (max-width: 480px) {
          .date-range-inputs {
            grid-template-columns: 1fr;
          }
        }

        .export-result {
          margin-top: 20px;
          padding: 15px;
          border-radius: 8px;
          font-size: 0.95em;
          animation: slideIn 0.3s ease-out;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .export-result.success {
          background-color: #d4edda;
          border-left: 4px solid #28a745;
          color: #155724;
        }

        .export-result.error {
          background-color: #f8d7da;
          border-left: 4px solid #dc3545;
          color: #721c24;
        }

        .result-icon {
          font-size: 20px;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @media (min-width: 768px) {
          .export-options {
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          }
        }
      `}</style>
    </div>
  );
};

export default DataExport;